import { computed, Injectable, signal } from '@angular/core';
import { ADD_CONTENT_STEPS } from '@features/playlists/constants';
import { AddContentData } from '@features/playlists/interfaces';

@Injectable({
    providedIn: 'root',
})
export class AddContentStepsService {
    /** Holds the draft steps data */
    private _draft = signal<Partial<AddContentData>>({});

    /** Holds the current step index */
    private _currentStepIdx = signal<number>(0);

    /** Map of step index to its validity */
    private _stepValidityMap = signal<Map<number, boolean>>(new Map());

    /** Read-only value for the current step index */
    public readonly currentStepIdx = this._currentStepIdx.asReadonly();

    /** Whether the current step is valid. Falls back to false if not yet evaluated. */
    public isStepValid = computed(() => {
        return this._stepValidityMap().get(this._currentStepIdx()) ?? false;
    });

    /**
     * Updates the current step index
     * @param idx - The new index to set
     */
    public setCurrentStepIdx(idx: number): void {
        this._currentStepIdx.set(idx);
    }

    /**
     * Updates the validity of a step.
     *
     * @param stepIdx - Index of the step to update.
     * @param isValid - New validity state.
     *
     * @remarks
     * Creates a new Map instance to preserve immutability and to re-trigger effects subscribed to this signal.
     */
    public setStepValidity(stepIdx: number, isValid: boolean): void {
        this._stepValidityMap.update((map) => new Map(map).set(stepIdx, isValid));
    }

    /**
     * Updates a single field in the draft state.
     *
     * @param stepKey - The draft property to update.
     * @param data - The new value for the specified property.
     *
     * @remarks
     * Performs an immutable update by merging the existing draft
     * with the provided field value.
     */
    public updateDraft<TKey extends keyof AddContentData>(
        stepKey: TKey,
        data: AddContentData[TKey],
    ): void {
        this._draft.update((draft) => ({
            ...draft,
            [stepKey]: data,
        }));
    }

    /**
     * Returns the current draft data used to prefill steps.
     *
     * @returns A partial AddContentData object containing saved draft values.
     *
     * @remarks
     * May be empty if no draft data has been set yet.
     */
    public getDraft(): Partial<AddContentData> {
        return this._draft();
    }

    /**
     * Resets the add-content workflow to its initial state.
     * Clears draft data, step validity map, and rewinds the current step index.
     */
    public clearDraft(): void {
        this._draft.set({});
        this._stepValidityMap.set(new Map());
        this._currentStepIdx.set(0);
    }
}
